import { useState } from "react";
import { Award, X, ZoomIn } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const CERTIFICATES = [
  {
    id: "majstor-frizer",
    title: "Majstor frizer",
    issuer: "Hrvatska obrtnička komora",
    date: "12. prosinca 2025.",
    location: "Zagreb",
    image: "https://customer-assets.emergentagent.com/job_lamaison-fp/artifacts/4606ra87_IMG_9959.jpeg",
  },
  {
    id: "express-svecane",
    title: "Express svečane frizure",
    issuer: "Neferhair Academy",
    date: "22. ožujka 2026.",
    location: "Osijek",
    image: "https://customer-assets.emergentagent.com/job_lamaison-fp/artifacts/gnthlahe_IMG_9957.jpeg",
  },
  {
    id: "hollywood-night",
    title: "Hollywood Night",
    issuer: "Maja Blanuša",
    date: "5. listopada 2025.",
    location: "",
    image: "https://customer-assets.emergentagent.com/job_lamaison-fp/artifacts/37zguc7c_IMG_9958.jpeg",
  },
  {
    id: "svecane-vecernje",
    title: "Svečane i večernje frizure",
    issuer: "Amela Fazlibegović",
    date: "21. siječnja 2024.",
    location: "",
    image: "https://customer-assets.emergentagent.com/job_lamaison-fp/artifacts/80bgv7b1_IMG_9956.jpeg",
  },
];

export const CertificatesSection = () => {
  const [openImage, setOpenImage] = useState(null);

  return (
    <section id="certifikati" className="section-pad bg-emerald-darker relative grain" data-testid="certificates-section">
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid md:grid-cols-12 gap-12 items-end mb-16">
          <div className="md:col-span-7">
            <span className="font-sans tracking-luxury text-[0.65rem] uppercase text-gold/80">— Certifikati & edukacije</span>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl lg:text-6xl text-[#F5EFE3] leading-[1.1]" data-testid="certificates-title">
              Stalna <em className="text-gold not-italic">edukacija</em>, dokazana znanjem.
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="font-serif text-base md:text-lg text-[#F5EFE3]/80 leading-relaxed">
              Filip Pitinac kontinuirano usavršava svoje tehnike kroz radionice najpoznatijih edukatora
              u regiji. Svaki certifikat — još jedan razlog da vaša kosa bude u sigurnim rukama.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gold/15 border border-gold/15">
          {CERTIFICATES.map((c, i) => (
            <button
              type="button"
              key={c.id}
              onClick={() => setOpenImage(c)}
              data-testid={`cert-card-${c.id}`}
              className="bg-emerald-deep p-6 md:p-7 group text-left transition-all duration-500 hover:bg-emerald-darker focus:outline-none focus:ring-1 focus:ring-gold/60"
            >
              <div className="relative aspect-square overflow-hidden bg-emerald-darker">
                <img
                  src={c.image}
                  alt={`Certifikat — ${c.title}`}
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-darker/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute top-3 right-3 w-9 h-9 border border-gold/50 bg-emerald-darker/60 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <ZoomIn className="w-4 h-4 text-gold" strokeWidth={1.5} />
                </span>
              </div>

              <div className="mt-5 flex items-start gap-3">
                <Award className="w-4 h-4 text-gold mt-1 shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <p className="font-sans tracking-wide-2 text-[0.6rem] uppercase text-gold/70 tabular-nums">
                    0{i + 1} · {c.date}
                  </p>
                  <h3 className="mt-1.5 font-serif text-lg md:text-xl text-[#F5EFE3] leading-snug">{c.title}</h3>
                  <p className="mt-1 font-sans text-xs text-[#B0C4B1]">
                    {c.issuer}{c.location ? ` · ${c.location}` : ""}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        <p className="mt-10 font-script text-2xl md:text-3xl text-gold/85 text-center">
          Vaša kosa u sigurnim rukama.
        </p>
      </div>

      {/* Lightbox dialog */}
      <Dialog open={!!openImage} onOpenChange={(o) => !o && setOpenImage(null)}>
        <DialogContent
          data-testid="cert-lightbox"
          className="bg-[#061A12] border border-[#C9A86A]/30 rounded-none max-w-4xl p-0 text-[#F5EFE3]"
        >
          {openImage && (
            <div className="relative">
              <button
                onClick={() => setOpenImage(null)}
                className="absolute top-3 right-3 w-10 h-10 z-20 border border-gold/40 bg-emerald-darker/80 backdrop-blur flex items-center justify-center hover:bg-gold hover:text-emerald-darker transition"
                aria-label="Zatvori"
                data-testid="cert-close"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <img
                src={openImage.image}
                alt={openImage.title}
                className="w-full h-auto max-h-[80vh] object-contain bg-emerald-darker"
              />
              <div className="p-6 md:p-8 border-t border-gold/15">
                <p className="font-sans tracking-luxury text-[0.65rem] uppercase text-gold">{openImage.date}{openImage.location ? ` · ${openImage.location}` : ""}</p>
                <h3 className="mt-2 font-serif font-light text-2xl md:text-3xl text-[#F5EFE3]">{openImage.title}</h3>
                <p className="mt-1 font-sans text-sm text-[#B0C4B1]">{openImage.issuer}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
